// TypeScript types for Experiment Management UI
// Based on MongoDB schema and design specification

export type ExperimentType =
    | 'Metadata Extraction'
    | 'validation_llm_judge'
    | 'Validation'
    | 'Inference'
    | 'Classification'
    | 'Search'
    | 'Scrape'
    | 'Download';

// LLM-based experiment types (require AI provider/model)
export type LLMExperimentType =
    | 'Metadata Extraction'
    | 'validation_llm_judge'
    | 'Validation'
    | 'Inference'
    | 'Classification';

// Engine-based experiment types (require engine_config)
export type EngineExperimentType =
    | 'Search'
    | 'Scrape'
    | 'Download';

// Mapping from task group to experiment type
export const TASK_GROUP_TO_EXPERIMENT_TYPE: Record<string, ExperimentType> = {
    'SEARCH': 'Search',
    'SCRAPE': 'Scrape',
    'SCRAPING': 'Scrape',
    'DOWNLOAD': 'Download',
    'CLASSIFICATION': 'Classification',
    'EXTRACTION': 'Metadata Extraction',
    'VALIDATION': 'Validation',
    'INFERENCE': 'Inference'
};

// Engine configuration interfaces for different experiment types
export interface SearchEngineConfig {
    search_engine: 'brave' | 'google' | 'bing';
    max_results?: number;
}

export interface ScrapeEngineConfig {
    crawler_engine: 'firecrawl' | 'playwright' | 'requests';
    batch_size?: number;
    firecrawl_config?: {
        timeout?: number;
        wait_for?: string;
        headers?: Record<string, string>;
    };
}

export interface DownloadEngineConfig {
    batch_size?: number;
    retry_on_errors?: boolean;
    wait_time?: number;
}

export type EngineConfig = SearchEngineConfig | ScrapeEngineConfig | DownloadEngineConfig;

export type ExperimentStatus =
    | 'draft'
    | 'pending'
    | 'running'
    | 'paused'
    | 'completed'
    | 'failed'
    | 'cancelled';

export interface ConfidenceStats {
    min: number;
    max: number;
    count: number;
    average: number;
}

export interface PropertyStats {
    title: number;
    author: number;
    date: number;
    keywords: number;
    subject: number;
    document_type: number;
}

export interface MachineInfo {
    hostname: string;
    platform: string;
    machine: string;
    processor: string;
    system: string;
    node: string;
}

export interface ExperimentData {
    record_id?: string;
    created_time?: string;
    prompt?: string;
    name?: string;
    provider?: string;
    model?: string;
    json_schema?: string;
    api_key?: string;
    endpoint?: string;
    tags: string[];
    type?: ExperimentType | string;
    strict?: boolean;
    context_size: number;
    reasoning: boolean;
    number_of_processed_documents: number;
    status?: ExperimentStatus;
    engine_config?: EngineConfig;
}

export interface ExperimentProgress {
    run_status: ExperimentStatus;
    total_items: number;
    processed_items: number;
    current_batch: number;
    total_batches: number;
    errors: ProgressError[];
    started_at?: string;
    finished_at?: string;
}

export interface ProgressError {
    item_id: string;
    error_message: string;
    timestamp: string;
}

export interface ExperimentMetrics {
    overall?: {
        accuracy: number;
        precision?: number;
        recall?: number;
        f1_score?: number;
    };
    by_field?: {
        [field: string]: {
            accuracy: number;
            found_rate: number;
            avg_confidence: number;
        };
    };
}

export interface IExperiment {
    _id: string;
    machine_hostname: string;
    experiment_id: string;
    experiment_created_time: string;
    experiment_data: ExperimentData;
    machine_info?: MachineInfo;
    created_timestamp: string;
    last_used_timestamp: string;
    progress?: ExperimentProgress;
    metrics?: ExperimentMetrics;

    // Validation aggregation fields
    confidence_score_distribution?: {
        overall: ConfidenceStats;
        title: ConfidenceStats;
        author: ConfidenceStats;
        date: ConfidenceStats;
        keywords: ConfidenceStats;
        subject: ConfidenceStats;
        document_type: ConfidenceStats;
    };
    overall_average_confidence?: number;
    overall_classification_confidence_avg?: number;
    overall_validation_rate?: number;
    property_average_confidence?: PropertyStats;
    property_found_counts?: PropertyStats;
    property_success_rates?: PropertyStats;
    property_total_counts?: PropertyStats;
    total_experiments_failed_validation?: number;
    total_experiments_in_storage?: number;
    total_experiments_passed_validation?: number;
    total_experiments_validated?: number;
    total_experiments_with_errors?: number;
    validation_batch_size?: number;
    validation_coverage?: number;
    validation_data_source?: string;
    validation_engine_version?: string;
    validation_include_already_validated?: boolean;
    validation_last_updated?: string;
    validation_machine_info?: MachineInfo;
    validation_note?: string;
    validation_retry_on_errors?: boolean;
    validation_review_older_than?: number;
    validation_revalidate_if_extraction_newer?: boolean;
}

export interface ValidationResult {
    field: string;
    value: string | string[] | null;
    found: boolean;
    confidence: number;
    matches?: string[];
}

export interface IReportExperiment {
    _id: string;
    record_id: string;
    experiment_id: string;
    experiment_storage_id: string;
    validation_results?: ValidationResult[];
    status?: string;
    created_at?: string;
    extracted_data?: Record<string, unknown>;
    processing_time?: number;
    error_message?: string;
}

export interface ExperimentsListResponse {
    experiments: IExperiment[];
    pagination: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
    };
    filters?: {
        types: string[];
        providers: string[];
        statuses: string[];
    };
}

export interface ExperimentFormData {
    experiment_id: string;
    type: ExperimentType | string;
    provider: string;
    model: string;
    endpoint?: string;
    tags: string[];
    typeConfig: TypeConfig;
    // AI Configuration (for LLM-based experiments)
    temperature?: number;
    maxTokens?: number;
    prompt?: string;
    json_schema?: string;
    // Engine Configuration (for engine-based experiments)
    engineConfig?: EngineConfig;
}

export interface TypeConfig {
    // Metadata Extraction specific
    fieldsToExtract?: string[];
    customPrompt?: string;
    temperature?: number;
    maxTokens?: number;

    // Validation specific
    groundTruthSource?: 'mongodb' | 'csv' | 'manual';
    confidenceThreshold?: number;
    matchingMode?: 'exact' | 'fuzzy' | 'semantic';

    // Classification specific
    categories?: string[];
    multiLabel?: boolean;

    // Inference specific
    inferenceType?: string;
    outputFormat?: string;
}

// Constants
export const EXPERIMENT_TYPES: ExperimentType[] = [
    'Metadata Extraction',
    'validation_llm_judge',
    'Validation',
    'Inference',
    'Classification',
    'Search',
    'Scrape',
    'Download'
];

// LLM-based experiment types (require AI provider/model configuration)
export const LLM_EXPERIMENT_TYPES: LLMExperimentType[] = [
    'Metadata Extraction',
    'validation_llm_judge',
    'Validation',
    'Inference',
    'Classification'
];

// Engine-based experiment types (require engine_config)
export const ENGINE_EXPERIMENT_TYPES: EngineExperimentType[] = [
    'Search',
    'Scrape',
    'Download'
];

// Helper to check if an experiment type is LLM-based
export const isLLMExperimentType = (type?: ExperimentType | string): boolean => {
    if (!type) return false;
    return LLM_EXPERIMENT_TYPES.includes(type as LLMExperimentType);
};

// Helper to check if an experiment type is engine-based
export const isEngineExperimentType = (type?: ExperimentType | string): boolean => {
    if (!type) return false;
    return ENGINE_EXPERIMENT_TYPES.includes(type as EngineExperimentType);
};

export const EXPERIMENT_STATUSES: ExperimentStatus[] = [
    'draft',
    'pending',
    'running',
    'paused',
    'completed',
    'failed',
    'cancelled'
];

export const PROVIDER_OPTIONS = [
    'azure_openai',
    'openai',
    'anthropic',
    'aws_bedrock'
];

export const VALIDATION_FIELDS = [
    'title',
    'author',
    'date',
    'keywords',
    'subject',
    'document_type'
];

// Helper functions
export const getProviderColor = (provider?: string): string => {
    switch (provider) {
        case 'azure_openai':
            return '#0078d4';
        case 'openai':
            return '#00a67d';
        case 'anthropic':
            return '#d97757';
        case 'aws_bedrock':
            return '#ff9900';
        default:
            return '#6b7280';
    }
};

export const getStatusColor = (status?: ExperimentStatus | string): string => {
    switch (status) {
        case 'draft':
            return '#6b7280';
        case 'pending':
            return '#f59e0b';
        case 'running':
            return '#3b82f6';
        case 'paused':
            return '#eab308';
        case 'completed':
            return '#22c55e';
        case 'failed':
            return '#ef4444';
        case 'cancelled':
            return '#9ca3af';
        default:
            return '#6b7280';
    }
};

export const getExperimentTypeIcon = (type?: ExperimentType | string): string => {
    switch (type?.toLowerCase()) {
        case 'classification':
            return 'category';
        case 'metadata extraction':
            return 'description';
        case 'inference':
            return 'psychology';
        case 'validation':
        case 'validation_llm_judge':
            return 'fact_check';
        case 'search':
            return 'search';
        case 'scrape':
            return 'language';
        case 'download':
            return 'download';
        default:
            return 'science';
    }
};

export const formatDate = (dateString?: string): string => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
    });
};

export const formatDateTime = (dateString?: string): string => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
};

export const formatDuration = (milliseconds: number): string => {
    const seconds = Math.floor(milliseconds / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);

    if (hours > 0) {
        return `${hours}h ${minutes % 60}m`;
    }
    if (minutes > 0) {
        return `${minutes}m ${seconds % 60}s`;
    }
    return `${seconds}s`;
};
